import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";

import { api } from "@/lib/api-client";
import { EMIRATES, type CrmLead, type OrderInput } from "@/lib/orders";
import { LocationPicker } from "./LocationPicker";
import { Card, Label } from "./ui";

/**
 * Customer block. Typing a mobile number looks the lead up in the CRM; picking
 * one fills the fields and links the order to the lead (`crmLeadId`).
 */
export function CustomerCard({
  draft,
  set,
  disabled,
  pushToCrm,
  setPushToCrm,
}: {
  draft: OrderInput;
  set: (patch: Partial<OrderInput>) => void;
  disabled: boolean;
  pushToCrm: boolean;
  setPushToCrm: (v: boolean) => void;
}) {
  const [hits, setHits] = useState<CrmLead[]>([]);
  const [open, setOpen] = useState(false);
  const [nameQuery, setNameQuery] = useState("");
  const [searching, setSearching] = useState(false);

  // Phone lookup: from 7 digits on, 400 ms after the last keystroke.
  useEffect(() => {
    if (disabled || draft.crmLeadId) return;
    const digits = draft.mobile.replace(/\D/g, "");
    if (digits.length < 7) {
      setHits([]);
      return;
    }
    const handle = setTimeout(() => {
      setSearching(true);
      api
        .crmLeads({ phone: digits })
        .then((rows) => {
          setHits(rows);
          setOpen(rows.length > 0);
        })
        .catch(() => setHits([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [draft.mobile, draft.crmLeadId, disabled]);

  // Free search: a mostly-numeric query is a phone number, anything else is name / company / email.
  useEffect(() => {
    if (disabled || nameQuery.trim().length < 2) return;
    const digits = nameQuery.replace(/\D/g, "");
    const isPhone = digits.length >= 6 && digits.length >= nameQuery.replace(/[\s+()-]/g, "").length - 1;
    if (isPhone && digits.length < 7) return;
    const handle = setTimeout(() => {
      setSearching(true);
      api
        .crmLeads(isPhone ? { phone: digits } : { q: nameQuery.trim() })
        .then((rows) => {
          setHits(rows);
          setOpen(true);
        })
        .catch(() => setHits([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [nameQuery, disabled]);

  const pick = (lead: CrmLead) => {
    set({
      customerName: lead.name || draft.customerName,
      company: lead.company || draft.company,
      mobile: lead.mobile ? `+${lead.mobile}` : draft.mobile,
      email: lead.email || draft.email,
      emirate: EMIRATES.find((e) => e.toLowerCase() === lead.city.toLowerCase()) ?? draft.emirate,
      crmLeadId: lead.leadId,
    });
    setOpen(false);
    setNameQuery("");
    setPushToCrm(true);
  };

  const field = (key: keyof OrderInput, label: string, props: Record<string, unknown> = {}) => (
    <label className="block">
      <Label>{label}</Label>
      <input
        className="field"
        disabled={disabled}
        value={(draft[key] as string | undefined) ?? ""}
        onChange={(e) => set({ [key]: e.target.value } as Partial<OrderInput>)}
        {...props}
      />
    </label>
  );

  return (
    <Card
      tag="1 · Customer"
      right={
        draft.crmLeadId ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-green/50 bg-green/15 px-2.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-green">
            CRM lead #{draft.crmLeadId}
            {!disabled && (
              <button type="button" aria-label="Unlink lead" onClick={() => set({ crmLeadId: null })}>
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ) : null
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="relative">
          <Label>Mobile / WhatsApp · needed to send</Label>
          <input
            className="field"
            dir="ltr"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            placeholder="+971 5x xxx xxxx"
            disabled={disabled}
            value={draft.mobile}
            onFocus={() => hits.length && setOpen(true)}
            onChange={(e) => set({ mobile: e.target.value })}
          />
          {searching && <span className="absolute right-3 top-8 text-[0.6rem] text-ice/50">CRM…</span>}
        </div>
        <div className="relative">
          <Label>Find in CRM by phone, name or email</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-ice/40" />
            <input
              className="field pl-8"
              disabled={disabled}
              value={nameQuery}
              placeholder="Phone, surname, company or email"
              onChange={(e) => setNameQuery(e.target.value)}
            />
          </div>
        </div>

        {open && hits.length > 0 && (
          <div className="glass-panel-strong absolute left-0 right-0 z-20 mt-16 max-h-64 overflow-y-auto p-1 sm:col-span-2">
            {hits.map((h) => (
              <button
                key={h.leadId}
                type="button"
                onClick={() => pick(h)}
                className="flex w-full flex-wrap items-baseline gap-x-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-white/8"
              >
                <span className="font-semibold text-paper">{h.name || "—"}</span>
                {h.company && <span className="text-ice/70">{h.company}</span>}
                <span className="num text-ice/60" dir="ltr">{h.mobile ? `+${h.mobile}` : ""}</span>
                <span className="text-ice/50">{h.email}</span>
                <span className="ml-auto text-[0.6rem] uppercase tracking-[0.14em] text-pink">{h.source}</span>
              </button>
            ))}
            <button type="button" onClick={() => setOpen(false)} className="w-full px-3 py-1.5 text-center text-[0.62rem] uppercase tracking-[0.14em] text-ice/50">
              close
            </button>
          </div>
        )}

        {field("customerName", "Full name *", { autoComplete: "off" })}
        {field("company", "Company / studio")}
        {field("email", "Email", { type: "email", dir: "ltr" })}
        <label className="block">
          <Label>Emirate</Label>
          <select className="field" disabled={disabled} value={draft.emirate ?? ""} onChange={(e) => set({ emirate: e.target.value })}>
            <option value="" className="bg-night">—</option>
            {EMIRATES.map((e) => (
              <option key={e} value={e} className="bg-night">{e}</option>
            ))}
          </select>
        </label>
        <div className="sm:col-span-2">{field("siteAddress", "Site address · needed to send")}</div>
        <LocationPicker
          lat={draft.lat}
          lng={draft.lng}
          address={draft.siteAddress}
          emirate={draft.emirate}
          disabled={disabled}
          onChange={(pin) => set(pin ? { lat: pin.lat, lng: pin.lng } : { lat: null, lng: null })}
          onUseAddress={(text) => set({ siteAddress: text.slice(0, 300) })}
        />
        {draft.crmLeadId && !disabled && (
          <label className="flex items-center gap-2 text-xs text-ice/75 sm:col-span-2">
            <input type="checkbox" className="h-4 w-4 accent-[#D6338F]" checked={pushToCrm} onChange={(e) => setPushToCrm(e.target.checked)} />
            Update the CRM lead with these details when saving
          </label>
        )}
      </div>
    </Card>
  );
}
